import { Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

interface EllipsisTooltipProps {
    children: string;
    maxWidth?: number;
    maxCharacters?: number;
    ignoreValidation?: boolean;
}

const EllipsisTooltip = ({
    children,
    maxWidth = 250,
    maxCharacters = 50,
    ignoreValidation = false,
}: EllipsisTooltipProps) => {
    const textRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const shouldTruncate = children?.length > maxCharacters;

    const displayText = useMemo(() => {
        if (!shouldTruncate || ignoreValidation) {
            return children;
        }

        return `${children.substring(0, maxCharacters)}...`;
    }, [children, maxCharacters, shouldTruncate, ignoreValidation]);

    useEffect(() => {
        if (!shouldTruncate) {
            setShowTooltip(false);
            return;
        }

        const element = textRef.current;

        if (element && !ignoreValidation) {
            setShowTooltip(element.scrollWidth > element.clientWidth);
        }
    }, [displayText, shouldTruncate, ignoreValidation]);

    const withEllipsis = useMemo(() => {
        return showTooltip || displayText?.endsWith('...')
    }, [displayText, showTooltip])

    return (
        <Tooltip
            title={withEllipsis ? children : ""}
            disableHoverListener={!withEllipsis}
        >
            <Typography
                ref={textRef}
                noWrap={withEllipsis}
                sx={{
                    maxWidth,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    ...(withEllipsis && {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }),
                }}
            >
                {displayText}
            </Typography>
        </Tooltip>
    );
};

export default EllipsisTooltip;